import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  linkPlugin,
  linkDialogPlugin,
  toolbarPlugin,
  BoldItalicUnderlineToggles,
  ListsToggle,
  BlockTypeSelect,
  CreateLink,
  type MDXEditorMethods,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { Camp } from '../../utils/types';
import { useUpdateCamp } from '../../api/organizerAdmin';
import './CampEditModal.css';

interface CampEditModalProps {
  camp: Camp;
  isOpen: boolean;
  onClose: () => void;
  organizerId: string;
}

const CampEditModal: React.FC<CampEditModalProps> = ({ camp, isOpen, onClose, organizerId }) => {
  const [name, setName] = useState(camp.name);
  const [description, setDescription] = useState(camp.description || '');
  const [additionalInfo, setAdditionalInfo] = useState(camp.additional_info || '');
  const [minAge, setMinAge] = useState<string>(camp.min_age?.toString() || '');
  const [maxAge, setMaxAge] = useState<string>(camp.max_age?.toString() || '');
  const [startTime, setStartTime] = useState(camp.start_time || '');
  const [endTime, setEndTime] = useState(camp.end_time || '');
  const [price, setPrice] = useState<string>(camp.price?.toString() || '');
  const [error, setError] = useState<string | null>(null);

  const descriptionRef = useRef<MDXEditorMethods>(null);
  const additionalInfoRef = useRef<MDXEditorMethods>(null);

  const updateCamp = useUpdateCamp(organizerId);

  useEffect(() => {
    setName(camp.name);
    setDescription(camp.description || '');
    setAdditionalInfo(camp.additional_info || '');
    setMinAge(camp.min_age?.toString() || '');
    setMaxAge(camp.max_age?.toString() || '');
    setStartTime(camp.start_time || '');
    setEndTime(camp.end_time || '');
    setPrice(camp.price?.toString() || '');
    setError(null);
    descriptionRef.current?.setMarkdown(camp.description || '');
    additionalInfoRef.current?.setMarkdown(camp.additional_info || '');
  }, [camp]);

  if (!isOpen) return null;

  const editorPlugins = [
    headingsPlugin(),
    listsPlugin(),
    linkPlugin(),
    linkDialogPlugin(),
    toolbarPlugin({
      toolbarContents: () => (
        <>
          <BlockTypeSelect />
          <BoldItalicUnderlineToggles />
          <ListsToggle />
          <CreateLink />
        </>
      ),
    }),
  ];

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Camp name is required.');
      return;
    }

    setError(null);
    try {
      await updateCamp.mutateAsync({
        campId: camp.id,
        data: {
          name: name.trim(),
          description: description || null,
          additional_info: additionalInfo || null,
          min_age: minAge ? parseInt(minAge, 10) : null,
          max_age: maxAge ? parseInt(maxAge, 10) : null,
          start_time: startTime || null,
          end_time: endTime || null,
          price: price ? parseFloat(price) : null,
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update camp.');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="camp-edit-modal">
        <button className="modal-close-btn" onClick={onClose} disabled={updateCamp.isPending}>
          <X />
        </button>

        <h2 className="camp-edit-modal__title">Edit Camp</h2>

        {error && <div className="message message--error">{error}</div>}

        <div className="camp-edit-modal__form">
          <div className="form-group">
            <label htmlFor="camp-name">Name</label>
            <input
              id="camp-name"
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={updateCamp.isPending}
            />
          </div>

          <div className="camp-edit-modal__field-row">
            <div className="form-group">
              <label htmlFor="camp-min-age">Min Age</label>
              <input
                id="camp-min-age"
                type="number"
                className="form-input"
                value={minAge}
                onChange={(e) => setMinAge(e.target.value)}
                disabled={updateCamp.isPending}
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="camp-max-age">Max Age</label>
              <input
                id="camp-max-age"
                type="number"
                className="form-input"
                value={maxAge}
                onChange={(e) => setMaxAge(e.target.value)}
                disabled={updateCamp.isPending}
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="camp-start-time">Start Time</label>
              <input
                id="camp-start-time"
                type="time"
                className="form-input"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={updateCamp.isPending}
              />
            </div>
            <div className="form-group">
              <label htmlFor="camp-end-time">End Time</label>
              <input
                id="camp-end-time"
                type="time"
                className="form-input"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={updateCamp.isPending}
              />
            </div>
            <div className="form-group">
              <label htmlFor="camp-price">Price ($)</label>
              <input
                id="camp-price"
                type="number"
                className="form-input"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                disabled={updateCamp.isPending}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <div className="camp-edit-modal__editor-wrapper">
              <MDXEditor
                ref={descriptionRef}
                markdown={description}
                onChange={setDescription}
                plugins={editorPlugins}
                readOnly={updateCamp.isPending}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Additional Info</label>
            <div className="camp-edit-modal__editor-wrapper">
              <MDXEditor
                ref={additionalInfoRef}
                markdown={additionalInfo}
                onChange={setAdditionalInfo}
                plugins={editorPlugins}
                readOnly={updateCamp.isPending}
              />
            </div>
          </div>
        </div>

        <div className="camp-edit-modal__actions">
          <button
            className="btn btn--secondary btn--md"
            onClick={onClose}
            disabled={updateCamp.isPending}
          >
            Cancel
          </button>
          <button
            className="btn btn--primary btn--md"
            onClick={handleSave}
            disabled={updateCamp.isPending}
          >
            {updateCamp.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampEditModal;
